import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart2, Download, Newspaper, Sparkles, Trophy } from "lucide-react";
import { reportsApi, type NewsBreakdown } from "../lib/api";
import { formatCurrency, formatSignedCurrency } from "../lib/format";
import { useI18n } from "../lib/i18n";

export default function ReportsPage() {
  const { t } = useI18n();
  const q = useQuery({ queryKey: ["reports-overview"], queryFn: () => reportsApi.overview() });

  const h = q.data?.headline;

  return (
    <>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-parchment-muted text-xs uppercase tracking-widest">{t("nav.reports")}</p>
          <h1 className="text-3xl font-bold text-parchment mt-1 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-accent" />
            {t("reports.title")}
          </h1>
        </div>
        {/* rel="noopener" NOT "noreferrer": Sanctum decides whether to apply
            session auth by matching the Referer (or Origin) against the
            stateful domains. A top-level navigation sends no Origin, so
            stripping the Referer made this look like a token request, the
            session cookie was ignored, and the PDF 401'd. noopener still
            blocks window.opener access. */}
        <a
          href="/journal/api/reports/pdf"
          target="_blank"
          rel="noopener"
          className="btn-gold"
        >
          <Download className="w-4 h-4" />
          {t("reports.downloadPdf")}
        </a>
      </header>

      {q.isLoading && <p className="text-parchment-muted">{t("common.loading")}</p>}
      {q.isError && (
        <p className="text-pnl-neg text-sm">{(q.error as Error)?.message ?? t("reports.error")}</p>
      )}

      {h && (
        <>
          {/* Headline */}
          <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label={t("reports.netProfit")} value={formatSignedCurrency(h.net_profit)} accent={h.net_profit >= 0 ? "text-pnl-pos" : "text-pnl-neg"} />
            <Stat label={t("reports.winRate")} value={`${h.win_rate_pct.toFixed(1)}%`} accent="text-accent" />
            <Stat label={t("nav.trades")} value={`${h.trade_count}`} sub={t("reports.winLoss", { wins: h.win_count, losses: h.loss_count })} />
            <Stat label={t("reports.profitFactor")} value={h.profit_factor != null ? h.profit_factor.toFixed(2) : "—"} />
            <Stat label={t("reports.avgWin")} value={formatCurrency(h.avg_win)} accent="text-pnl-pos" />
            <Stat label={t("reports.avgLoss")} value={formatCurrency(h.avg_loss)} accent="text-pnl-neg" />
            <Stat label={t("reports.expectancy")} value={formatSignedCurrency(h.expectancy)} accent={h.expectancy >= 0 ? "text-pnl-pos" : "text-pnl-neg"} />
            <Stat label={t("reports.grossPl")} value={`${formatCurrency(h.gross_profit)} / ${formatCurrency(h.gross_loss)}`} />
          </section>

          {/* By symbol */}
          <section className="glass p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-parchment mb-3 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-accent" />
              {t("reports.bySymbol")}
            </h2>
            {q.data && q.data.by_symbol.length === 0 ? (
              <p className="text-parchment-muted text-sm">{t("reports.noSymbolData")}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm tabular">
                  <thead className="text-parchment-muted text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-start py-2 pe-4">{t("reports.colSymbol")}</th>
                      <th className="text-end py-2 pe-4">{t("nav.trades")}</th>
                      <th className="text-end py-2 pe-4">{t("reports.colWinPct")}</th>
                      <th className="text-end py-2">{t("reports.colNetPl")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data!.by_symbol.map((r) => (
                      <tr key={r.symbol} className="border-t border-glass">
                        <td className="py-2 pe-4 font-mono text-parchment">{r.symbol}</td>
                        <td className="py-2 pe-4 text-end text-parchment-muted">{r.trades}</td>
                        <td className="py-2 pe-4 text-end">{r.win_rate_pct.toFixed(1)}%</td>
                        <td className={`py-2 text-end font-semibold ${r.net_pl >= 0 ? "text-pnl-pos" : "text-pnl-neg"}`}>
                          {formatSignedCurrency(r.net_pl)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* News vs non-news */}
          <section className="glass p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-parchment mb-1 flex items-center gap-2">
              <Newspaper className="w-5 h-5 text-accent" />
              {t("reports.byNews")}
            </h2>
            <p className="text-xs text-parchment-muted mb-4">{t("reports.byNewsSub")}</p>

            {/* Rings answer "which is better" at a glance; the table below
                carries the detail the rings cannot (net P/L, average). */}
            <div className="flex flex-wrap justify-center gap-8 sm:gap-12 mb-6">
              {(q.data?.by_news ?? []).map((r: NewsBreakdown) => (
                <SuccessDonut
                  key={r.bucket}
                  title={t(r.bucket === "news" ? "reports.newsGroup" : "reports.normalGroup")}
                  wins={r.wins}
                  losses={r.losses}
                  rate={r.win_rate_pct}
                />
              ))}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm tabular">
                <thead className="text-parchment-muted text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-start py-2 pe-4">{t("reports.colGroup")}</th>
                    <th className="text-end py-2 pe-4">{t("nav.trades")}</th>
                    <th className="text-end py-2 pe-4">{t("reports.colWinPct")}</th>
                    <th className="text-end py-2 pe-4">{t("reports.colAvg")}</th>
                    <th className="text-end py-2">{t("reports.colNetPl")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(q.data?.by_news ?? []).map((r: NewsBreakdown) => (
                    <tr key={r.bucket} className="border-t border-glass">
                      <td className="py-2 pe-4 text-parchment">
                        {t(r.bucket === "news" ? "reports.newsGroup" : "reports.normalGroup")}
                      </td>
                      <td className="py-2 pe-4 text-end text-parchment-muted">
                        {r.trades}
                        {r.trades > 0 && (
                          <span className="text-parchment-muted/70"> ({r.wins}/{r.losses})</span>
                        )}
                      </td>
                      {/* A bucket with no trades has no rate to state -- show a
                          dash rather than an authoritative-looking 0%. */}
                      <td className="py-2 pe-4 text-end">
                        {r.win_rate_pct == null ? "—" : `${r.win_rate_pct.toFixed(1)}%`}
                      </td>
                      <td className="py-2 pe-4 text-end text-parchment-muted">
                        {r.avg_pl == null ? "—" : formatSignedCurrency(r.avg_pl)}
                      </td>
                      <td className={`py-2 text-end font-semibold ${r.net_pl >= 0 ? "text-pnl-pos" : "text-pnl-neg"}`}>
                        {r.trades === 0 ? "—" : formatSignedCurrency(r.net_pl)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Day of week + hour heat */}
          <div className="grid gap-4 lg:grid-cols-2">
            <section className="glass p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-parchment mb-3 flex items-center gap-2">
                <BarChart2 className="w-5 h-5 text-accent" />
                {t("reports.byDow")}
              </h2>
              <DivergingBars rows={q.data!.by_dow.map((r) => ({ label: r.label, trades: r.trades, net_pl: r.net_pl }))} />
            </section>
            <section className="glass p-4 sm:p-6">
              <h2 className="text-lg font-semibold text-parchment mb-3 flex items-center gap-2">
                <BarChart2 className="w-5 h-5 text-accent" />
                {t("reports.byHour")}
              </h2>
              <DivergingBars
                rows={q.data!.by_hour.map((r) => ({ label: String(r.hour).padStart(2, "0"), trades: r.trades, net_pl: r.net_pl }))}
                dense
              />
            </section>
          </div>

          {/* Monthly */}
          <section className="glass p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-parchment mb-3">{t("reports.monthlyPl")}</h2>
            {q.data!.monthly.length === 0 ? (
              <p className="text-parchment-muted text-sm">{t("reports.noMonths")}</p>
            ) : (
              <DivergingBars
                rows={q.data!.monthly.map((r) => ({ label: r.month, trades: r.trades, net_pl: r.net_pl }))}
                labelWidth="w-16"
              />
            )}
          </section>
        </>
      )}
    </>
  );
}

function Stat({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: string;
}) {
  return (
    <div className="glass p-4">
      <p className="label">{label}</p>
      <p className={`mt-2 text-2xl font-bold tabular ${accent ?? "text-parchment"}`}>{value}</p>
      {sub && <p className="text-xs text-parchment-muted mt-1">{sub}</p>}
    </div>
  );
}

/**
 * Simple horizontal bar chart. Positive bars go emerald, negative go rose.
 * `dense` shrinks height for the 24-hour view.
 */
/**
 * Diverging bars on a SHARED ZERO BASELINE.
 *
 * The previous version grew profits from the left edge and losses from the
 * right, so a -$500 bar and a +$500 bar were mirror images with no common
 * origin -- you could not see at a glance which buckets were net positive, let
 * alone compare their sizes. Every bar is now measured from one centre line, so
 * length is directly comparable and side carries the sign.
 *
 * Scale is symmetric (±maxAbs) so that a profit and a loss of equal size draw
 * equal-length bars.
 */
/**
 * Success-rate donut: wins vs losses as part-to-whole, with the rate itself as
 * the centre figure.
 *
 * One donut PER BUCKET rather than a single two-slice pie of news vs non-news:
 * a two-slice pie would only show how much you trade the news, which is not a
 * success rate at all. Two rings side by side put the two rates next to each
 * other, which is the actual comparison.
 */
function SuccessDonut({
  title,
  wins,
  losses,
  rate,
}: {
  title: string;
  wins: number;
  losses: number;
  rate: number | null;
}) {
  const { t } = useI18n();
  const total = wins + losses;
  const R = 46;
  const C = 2 * Math.PI * R;
  // A 2px surface gap separates the two arcs, per mark spec -- never a stroke
  // outline around them.
  const GAP = total > 0 && wins > 0 && losses > 0 ? 3 : 0;
  const winLen = total > 0 ? (wins / total) * C : 0;
  const lossLen = total > 0 ? (losses / total) * C : 0;

  return (
    <div className="flex flex-col items-center gap-3">
      <p className="label text-center">{title}</p>

      <div className="relative">
        <svg viewBox="0 0 120 120" className="w-32 h-32" role="img" aria-label={title}>
          {/* Track */}
          <circle
            cx="60" cy="60" r={R} fill="none" strokeWidth="10"
            stroke="rgb(var(--border-soft))"
          />
          {total > 0 && (
            <g transform="rotate(-90 60 60)">
              <circle
                cx="60" cy="60" r={R} fill="none" strokeWidth="10" strokeLinecap="butt"
                className="stroke-pnl-pos"
                strokeDasharray={`${Math.max(0, winLen - GAP)} ${C - Math.max(0, winLen - GAP)}`}
              />
              <circle
                cx="60" cy="60" r={R} fill="none" strokeWidth="10" strokeLinecap="butt"
                className="stroke-pnl-neg"
                strokeDasharray={`${Math.max(0, lossLen - GAP)} ${C - Math.max(0, lossLen - GAP)}`}
                strokeDashoffset={-winLen}
              />
            </g>
          )}
        </svg>

        {/* Centre figure: proportional digits, not tabular -- this is a hero
            number, not a column of aligned values. */}
        <div className="absolute inset-0 grid place-items-center pointer-events-none">
          <div className="text-center">
            <p className="text-2xl font-bold text-parchment leading-none">
              {rate == null ? "—" : `${rate.toFixed(0)}%`}
            </p>
            <p className="text-[10px] text-parchment-muted mt-1">
              {total === 0 ? t("reports.noTradesBucket") : t("calendar.tradesCount", { count: total })}
            </p>
          </div>
        </div>
      </div>

      {/* Legend: identity never by colour alone. */}
      <div className="flex items-center gap-4 text-xs">
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-pnl-pos shrink-0" aria-hidden="true" />
          <span className="text-parchment-muted">{t("reports.wins", { count: wins })}</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm bg-pnl-neg shrink-0" aria-hidden="true" />
          <span className="text-parchment-muted">{t("reports.losses", { count: losses })}</span>
        </span>
      </div>
    </div>
  );
}

function DivergingBars({
  rows,
  dense,
  // Label column width. "Mon"/"07" fit w-10; "2026-04" does not and wrapped
  // onto two lines, so the monthly chart passes a wider column.
  labelWidth = "w-10",
}: {
  rows: Array<{ label: string; trades: number; net_pl: number }>;
  dense?: boolean;
  labelWidth?: string;
}) {
  const { t } = useI18n();
  const [hover, setHover] = useState<number | null>(null);
  const maxAbs = Math.max(1, ...rows.map((r) => Math.abs(r.net_pl)));

  return (
    <div className="relative">
      <ul className={dense ? "space-y-0.5" : "space-y-1"}>
        {rows.map((r, i) => {
          // Half the track per side, so the centre is true zero.
          const pct = (Math.abs(r.net_pl) / maxAbs) * 50;
          const pos = r.net_pl > 0;
          const empty = r.trades === 0;
          return (
            <li
              key={i}
              className="flex items-center gap-2 text-xs rounded-md px-1 -mx-1 py-0.5 transition-colors"
              style={hover === i ? { backgroundColor: "rgb(var(--surface-2))" } : undefined}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(i)}
              onBlur={() => setHover(null)}
              tabIndex={0}
            >
              <span className={`${labelWidth} text-parchment-muted shrink-0 tabular whitespace-nowrap`}>
                {r.label}
              </span>

              <div className="flex-1 relative h-4">
                {/* Zero rule: a solid hairline, one shade off the surface. */}
                {/* -inset-y-1 makes each row's segment meet its neighbours
                    across the row gap, so the zero rule reads as one solid
                    hairline rather than a dashed one. */}
                <div
                  className="absolute -inset-y-1 start-1/2 w-px -translate-x-1/2"
                  style={{ backgroundColor: "rgb(var(--border-mid))" }}
                  aria-hidden="true"
                />
                {!empty && (
                  <div
                    className={
                      "absolute top-1/2 -translate-y-1/2 h-2.5 " +
                      (pos
                        ? "start-1/2 bg-pnl-pos rounded-e"
                        : "end-1/2 bg-pnl-neg rounded-s")
                    }
                    // Minimum 1px so an exact-zero P/L on a traded bucket is
                    // still visible as a mark rather than vanishing.
                    style={{ width: `max(1px, ${pct}%)` }}
                  />
                )}
              </div>

              <span
                className={`w-24 text-end tabular ${
                  empty
                    ? "text-parchment-muted/50"
                    : pos
                      ? "text-pnl-pos"
                      : "text-pnl-neg"
                }`}
              >
                {empty ? "—" : formatSignedCurrency(r.net_pl)}
              </span>
              <span className="w-8 text-end text-parchment-muted shrink-0 tabular">
                {empty ? "" : r.trades}
              </span>
            </li>
          );
        })}
      </ul>

      {hover !== null && rows[hover] && (
        <p className="mt-2 text-[11px] text-parchment-muted border-t border-glass pt-2">
          <span className="text-parchment font-semibold">{rows[hover].label}</span>
          {" · "}
          {rows[hover].trades === 0
            ? t("reports.noTradesBucket")
            : t("reports.bucketSummary", {
                trades: rows[hover].trades,
                net: formatSignedCurrency(rows[hover].net_pl),
                avg: formatSignedCurrency(rows[hover].net_pl / rows[hover].trades),
              })}
        </p>
      )}
    </div>
  );
}
